import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  OutlinedInput,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Translate from "../../utils/Translate";
import { useCallback, useEffect, useState } from "react";
import AutocompleteSearchField from "../AutocompleteSearchField";
import { useApi } from "../../utils/Api";
import { MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import Preloader from "../Preloader";
import { addressType } from "../../utils/address";
import { Cancel, Clear } from "@mui/icons-material";
import ButtonWithSpinner from "../ButtonWithSpinner";
import YesOrNoDialog from "../YesOrNoDialog";
import { accessKeys, hasAccess } from "../../utils/userAccess";

const adressTypes = [addressType.billing, addressType.visiting, addressType.postal, addressType.delivery];

export default function AddOrUpdateAddressDialog({
  existingAddress,
  actorId,
  mode,
  onCancel,
  showSelectAddressType,
  handleCloseAddOrUpdateAddressDialog,
  contacts,
  existingPreferredAddressesOfContacts,
}) {
  const [actorAddressStreet1, setActorAddressStreet1] = useState(existingAddress?.actorAddressStreet1 ?? "");
  const [actorAddressStreet2, setActorAddressStreet2] = useState(existingAddress?.actorAddressStreet2 ?? "");
  const [actorAddressZipCode, setActorAddressZipCode] = useState(existingAddress?.actorAddressZipCode ?? "");
  const [actorAddressCity, setActorAddressCity] = useState(existingAddress?.actorAddressCity ?? "");
  const [actorAddressCountry, setActorAddressCountry] = useState(existingAddress?.actorAddressCountry ?? "");
  const [actorAddressTypeId, setActorAddressTypeId] = useState(existingAddress?.actorAddressType ?? "");
  const [actorAddressId] = useState(existingAddress?.actorAddressId);
  const [contactActorIds, setContactActorIds] = useState(
    (existingAddress?.preferredAddresses ?? [])
      .filter((pa) => contacts.some((c) => c.actorId === pa.actorId))
      .map((pa) => pa.actorId),
  );
  const [pendingContactActorIds, setPendingContactActorIds] = useState(null);
  const [countries, setCountries] = useState([]);
  const api = useApi();

  const countrySearch = useCallback(
    async (searchText) => {
      const lang = localStorage.lang ?? 29;
      const search = encodeURIComponent(searchText);
      const url = `${import.meta.env.VITE_MAIN_URL}countries?searchText=${search}&languageCode=${lang}`;
      const result = await api.fetch(url, false, "GET");
      result.sort((a, b) => (a.alpha2 === "SE" ? -1 : b.alpha2 === "SE" ? 1 : a.name.localeCompare(b.name)));
      return result;
    },
    [api],
  );

  useEffect(() => {
    countrySearch("").then((result) => setCountries(result));
  }, [countrySearch]);

  function buildRequest() {
    return {
      ActorAddressId: actorAddressId,
      ActorId: actorId,
      AddressStreet1: actorAddressStreet1,
      AddressStreet2: actorAddressStreet2,
      ZipCode: actorAddressZipCode,
      City: actorAddressCity,
      Country: actorAddressCountry,
      AddressType: actorAddressTypeId,
      PreferredAddresses: contactActorIds.map((id) => ({
        actorId: id,
        actorAddressId: actorAddressId,
        actorAddressActorId: actorId,
        actorAddressType: actorAddressTypeId,
      })),
    };
  }

  async function handleSave() {
    const method = !!actorAddressId ? "PUT" : "POST";
    const request = buildRequest();
    const response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}actors/address`, request, method);

    if (response.isSuccessful) {
      await handleCloseAddOrUpdateAddressDialog();
    }
  }

  function hasExisitingPreferredAddressOfSameType(contactActorId) {
    if (!existingPreferredAddressesOfContacts || existingPreferredAddressesOfContacts.length === 0) return false;

    const otherPreferredAddresses = existingPreferredAddressesOfContacts.filter(
      (epa) => epa.actorAddressId != actorAddressId && epa.actorId == contactActorId,
    );

    const isDeliveryType = actorAddressTypeId == addressType.delivery || actorAddressTypeId == addressType.postal;
    return isDeliveryType
      ? otherPreferredAddresses.some(
          (x) => x.actorAddressType == addressType.delivery || x.actorAddressType == addressType.postal,
        )
      : otherPreferredAddresses.some((x) => x.actorAddressType == actorAddressTypeId);
  }

  function handlePreferredAddressSelectionChange(event) {
    console.log("handlePreferredAddressSelectionChange");
    const newContactActorIds = event?.target?.value ?? [];
    const addedContactActorIds = newContactActorIds.filter((id) => !contactActorIds.includes(id));
    const someAddedHasExisting = addedContactActorIds.some((id) => hasExisitingPreferredAddressOfSameType(id));
    if (someAddedHasExisting) {
      setPendingContactActorIds(newContactActorIds);
    } else {
      setContactActorIds(newContactActorIds);
    }
  }

  return (
    <>
      <Dialog open={true} className="smallCourseDialog">
        <DialogTitle>{actorAddressId ? Translate.get("EditAddress") : Translate.get("AddAddress")}</DialogTitle>
        <DialogContent>
          <Grid container direction="column" spacing={2} marginTop={0.1}>
            <Grid style={{ marginTop: "1em" }}>
              <TextField
                fullWidth
                value={actorAddressStreet1}
                InputProps={{ spellCheck: false }}
                onChange={(event) => setActorAddressStreet1(event.target.value)}
                label={Translate.get("Address1")}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid>
              <TextField
                fullWidth
                value={actorAddressStreet2}
                InputProps={{ spellCheck: false }}
                onChange={(event) => setActorAddressStreet2(event.target.value)}
                label={Translate.get("Address2")}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid container spacing={2}>
              <Grid size={5}>
                <TextField
                  fullWidth
                  value={actorAddressZipCode}
                  InputProps={{ spellCheck: false }}
                  onChange={(event) => setActorAddressZipCode(event.target.value)}
                  label={Translate.get("ZipCode")}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
              <Grid size={7}>
                <TextField
                  fullWidth
                  value={actorAddressCity}
                  InputProps={{ spellCheck: false }}
                  onChange={(event) => setActorAddressCity(event.target.value)}
                  label={Translate.get("AddressCity")}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
            </Grid>
            <Grid>
              {/* Some addresses will have real country names, others have some other kind of data.
                              Here we show a list of existing countries if the value matches one or is empty.
                              But for all existing addresses with other content we will show a text field. */}
              {!actorAddressCountry?.trim() || countries.find((c) => c.name === actorAddressCountry) ? (
                <AutocompleteSearchField
                  requireSelection
                  disableAddAsNew
                  label={Translate.get("Country")}
                  defaultOptions={countries}
                  value={
                    actorAddressCountry && countries && countries.length > 0
                      ? countries.find((c) => c.name === actorAddressCountry)
                      : ""
                  }
                  onValueChange={(option) => setActorAddressCountry(option?.name)}
                  getOptionLabel={(option) => (option ? `${option.name} (${option.alpha2})` : "")}
                  renderOption={(option) => `${option.name} (${option.alpha2})`}
                  keyPropName="name"
                  search={countrySearch}
                  textFieldProps={{
                    required: false,
                  }}
                  hideOptionsFilter={actorAddressCountry && ((option) => option.name === actorAddressCountry.trim())}
                />
              ) : (
                <TextField
                  fullWidth
                  value={actorAddressCountry}
                  onChange={(event) => setActorAddressCountry(event.target.value)}
                  label={Translate.get("Country")}
                  inputProps={{ maxLength: 50 }}
                />
              )}

              {showSelectAddressType === true && (
                <FormControl fullWidth style={{ marginTop: "1em" }}>
                  <InputLabel id="adress-type-label">{Translate.get("AddressType")}</InputLabel>
                  <Select
                    labelId="adress-type-label"
                    label={Translate.get("AddressType")}
                    value={actorAddressTypeId}
                    onChange={(event) => setActorAddressTypeId(event.target.value)}
                  >
                    {adressTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {Translate.getActorAddressType(type)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {(!actorAddressTypeId ||
                actorAddressTypeId == addressType.delivery ||
                actorAddressTypeId == addressType.postal) && (
                <FormControl fullWidth style={{ marginTop: "1em" }} disabled={!actorAddressTypeId}>
                  <InputLabel id="preferred-address-label" disabled={!actorAddressTypeId}>
                    {Translate.get("PreferredAddressLabel")}
                  </InputLabel>
                  <Select
                    disabled={!actorAddressTypeId}
                    labelId="preferred-address-label"
                    id="preferred-address"
                    multiple
                    value={contactActorIds}
                    onChange={handlePreferredAddressSelectionChange}
                    input={<OutlinedInput id="select-multiple-chip" label={Translate.get("PreferredAddressLabel")} />}
                    renderValue={(selectedIds) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selectedIds.map((id) => (
                          <Chip
                            key={id}
                            label={contacts.find((c) => c.actorId == id).actorName}
                            clickable
                            deleteIcon={<Cancel onMouseDown={(event) => event.stopPropagation()} />}
                            onDelete={() => setContactActorIds((prev) => prev.filter((prevId) => prevId != id))}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {contacts.map((contact) => (
                      <MenuItem key={contact.actorId} value={contact.actorId}>
                        {contact.actorName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={onCancel}>
            {Translate.get("Cancel")}
          </Button>
          <ButtonWithSpinner
            variant="contained"
            onClick={handleSave}
            disabled={
              !actorAddressStreet1 ||
              !actorAddressStreet2 ||
              !actorAddressZipCode ||
              !actorAddressCity ||
              (showSelectAddressType && !actorAddressTypeId) ||
              (hasAccess(accessKeys.isMASystem) && !actorAddressCountry?.trim())
            }
          >
            {Translate.get("Save")}
          </ButtonWithSpinner>
        </DialogActions>
      </Dialog>
      <YesOrNoDialog
        open={!!pendingContactActorIds && pendingContactActorIds.length > 0}
        text={Translate.get("ReplacePreferredAddressQuestion")}
        onYes={() => {
          setContactActorIds(pendingContactActorIds);
          setPendingContactActorIds(null);
        }}
        onNo={() => setPendingContactActorIds(null)}
      />
    </>
  );
}
